import { useEffect, useRef, useSyncExternalStore } from 'react';
import { io, Socket } from 'socket.io-client';

export interface ConnectionStatus {
  connected: boolean;
  // True once the socket has connected at least once and has since dropped — deliberately
  // false during the very first connection attempt on cold start, so the UI never flashes
  // "reconnecting" before the app has ever been online.
  reconnecting: boolean;
  // Increments once per reconnect (never on the first connect). Data hooks watch this NUMBER
  // (not a boolean) as a dependency, so a plain useEffect fires exactly once per reconnect —
  // handy for "refetch authoritative state to catch up on whatever was missed while dropped".
  reconnectCount: number;
}

export interface SocketSingleton {
  getSocket(): Socket;
  useSocket(): Socket;
  useMatchSocket(matchId: string | null, handlers: Record<string, (data: unknown) => void>): void;
  // Read-only counterpart to useMatchSocket — joins the same room via a DIFFERENT event pair
  // (match:watch/match:unwatch) so a spectator/bracket-card subscription never contends for
  // the server's scorer-role assignment, which is keyed off match:join specifically (see
  // apps/api/src/index.ts). Use this for anything that only displays live match state.
  useWatchMatchSocket(matchId: string | null, handlers: Record<string, (data: unknown) => void>): void;
  useTournamentSocket(tournamentId: string | null, handlers: Record<string, (data: unknown) => void>): void;
  // Plain event listeners on the shared socket, no room join/leave involved — for broadcasts
  // that reach every authenticated socket via a room it's auto-joined to server-side just by
  // connecting (e.g. its own `user:${userId}` room, see apps/api/src/index.ts), so there's
  // nothing for the client to explicitly join. Handlers are re-registered whenever the
  // `events` key list changes; pass a stable/memoized handler map where possible.
  useSocketEvents(handlers: Record<string, (data: unknown) => void>): void;
  useConnectionStatus(): ConnectionStatus;
  // Registers where to pull the current access token from. Set once the app knows who's
  // signed in (see AuthGate/AuthenticatedApp) — the socket itself is created lazily on first
  // use, by which point this has already been set, but `auth` is re-invoked by socket.io on
  // every (re)connection attempt so it always sends a fresh, non-expired token.
  setAccessTokenGetter(getAccessToken: () => Promise<string | null>): void;
}

interface RoomEntry {
  joinEvent: string;
  leaveEvent: string;
  id: string;
  refCount: number;
}

// Each platform supplies its own URL-resolution strategy (e.g. same-origin on
// web in production vs. a configured API host on mobile).
export function createSocketSingleton(getUrl: () => string): SocketSingleton {
  let socket: Socket | null = null;
  let getAccessToken: () => Promise<string | null> = () => Promise.resolve(null);

  // ── Room registry — re-joined on every reconnect, since socket.io does not persist room
  // membership across a dropped/re-established connection (a reconnect is a brand-new
  // connection server-side). Refcounted so two hook instances that happen to want the same
  // room (e.g. React StrictMode's double-invoke in dev) don't have one's unmount evict the
  // other. Keyed by `${joinEvent}:${id}` so match rooms, tournament rooms, and watch-rooms
  // never collide even if two subsystems happened to reuse the same id string.
  const rooms = new Map<string, RoomEntry>();

  function roomKey(joinEvent: string, id: string): string { return `${joinEvent}:${id}`; }

  function joinRoom(joinEvent: string, leaveEvent: string, id: string): void {
    const key = roomKey(joinEvent, id);
    const existing = rooms.get(key);
    if (existing) { existing.refCount++; return; }
    rooms.set(key, { joinEvent, leaveEvent, id, refCount: 1 });
    // Emit unconditionally (not gated on socket.connected) — socket.io-client buffers
    // emits made before the first connect and flushes them once connected, exactly like the
    // original pre-registry implementation relied on. Introducing a "wait for connect, then
    // catch up via the room registry" path here as well would just be a second, redundant
    // source of the same join on first connect — reconnect catch-up (the actual point of the
    // registry) is handled separately below, gated to real reconnects only.
    getSocket().emit(joinEvent, id);
  }

  function leaveRoom(joinEvent: string, id: string): void {
    const key = roomKey(joinEvent, id);
    const existing = rooms.get(key);
    if (!existing) return;
    existing.refCount--;
    if (existing.refCount <= 0) {
      rooms.delete(key);
      getSocket().emit(existing.leaveEvent, id);
    }
  }

  // ── Connection status — a fresh object on every change (never mutated in place) so
  // useSyncExternalStore's reference-equality check reliably detects updates.
  let everConnected = false;
  let status: ConnectionStatus = { connected: false, reconnecting: false, reconnectCount: 0 };
  const statusListeners = new Set<() => void>();

  function setStatus(next: ConnectionStatus): void {
    status = next;
    statusListeners.forEach((l) => l());
  }

  function setAccessTokenGetter(fn: () => Promise<string | null>): void {
    getAccessToken = fn;
  }

  function getSocket(): Socket {
    if (!socket) {
      socket = io(getUrl(), {
        path: '/socket.io/',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        auth: (cb) => {
          getAccessToken().then((token) => cb({ token })).catch(() => cb({}));
        },
      });

      socket.on('connect', () => {
        const wasReconnect = everConnected;
        everConnected = true;
        setStatus({ connected: true, reconnecting: false, reconnectCount: wasReconnect ? status.reconnectCount + 1 : status.reconnectCount });
        // Re-join every room this client cared about — the server has no memory of a
        // previous connection's room membership, so this is the fix for the "client silently
        // stops receiving events after any reconnect" bug. Only on a genuine RECONNECT —
        // the very first connect already delivers every room's join event via joinRoom's own
        // unconditional emit (buffered client-side by socket.io-client until this first
        // connect fires), so re-emitting here too would just be a redundant duplicate.
        if (wasReconnect) rooms.forEach((r) => socket!.emit(r.joinEvent, r.id));
      });
      socket.on('disconnect', () => {
        setStatus({ connected: false, reconnecting: everConnected, reconnectCount: status.reconnectCount });
      });
      socket.io.on('reconnect_attempt', () => {
        if (everConnected) setStatus({ ...status, connected: false, reconnecting: true });
      });
    }
    return socket;
  }

  function useSocket(): Socket {
    return getSocket();
  }

  // Generic room-join primitive — joins `id` via `joinEvent`/`leaveEvent` for the lifetime of
  // this hook instance (or until `id` changes), and re-joins automatically on reconnect via
  // the module-level registry above.
  function useRoomSocket(
    joinEvent: string,
    leaveEvent: string,
    id: string | null,
    handlers: Record<string, (data: unknown) => void>,
  ): void {
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;

    useEffect(() => {
      if (!id) return;
      const s = getSocket();
      joinRoom(joinEvent, leaveEvent, id);

      const entries = Object.entries(handlersRef.current);
      entries.forEach(([event, handler]) => s.on(event, handler));

      return () => {
        leaveRoom(joinEvent, id);
        entries.forEach(([event, handler]) => s.off(event, handler));
      };
    }, [joinEvent, leaveEvent, id]);
  }

  function useMatchSocket(
    matchId: string | null,
    handlers: Record<string, (data: unknown) => void>,
  ): void {
    useRoomSocket('match:join', 'match:leave', matchId, handlers);
  }

  function useWatchMatchSocket(
    matchId: string | null,
    handlers: Record<string, (data: unknown) => void>,
  ): void {
    useRoomSocket('match:watch', 'match:unwatch', matchId, handlers);
  }

  function useTournamentSocket(
    tournamentId: string | null,
    handlers: Record<string, (data: unknown) => void>,
  ): void {
    useRoomSocket('tournament:join', 'tournament:leave', tournamentId, handlers);
  }

  function useSocketEvents(handlers: Record<string, (data: unknown) => void>): void {
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;
    // Keyed on the sorted event-name list (not on `handlers` itself, which is a fresh object
    // every render for most callers) — re-registers only if the actual SET of events this
    // caller cares about changes, which for every current call site never happens after
    // mount, so this is effectively "register once, always call the latest handler".
    const eventKey = Object.keys(handlers).sort().join(',');

    useEffect(() => {
      const s = getSocket();
      const events = Object.keys(handlersRef.current);
      const wrapped = events.map((event) => {
        const fn = (data: unknown) => handlersRef.current[event]?.(data);
        s.on(event, fn);
        return { event, fn };
      });
      return () => { wrapped.forEach(({ event, fn }) => s.off(event, fn)); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventKey]);
  }

  function useConnectionStatus(): ConnectionStatus {
    // Ensure the socket (and therefore its connect/disconnect listeners) exists even if the
    // first thing to mount is a component that only cares about connection status.
    getSocket();
    return useSyncExternalStore(
      (onChange) => { statusListeners.add(onChange); return () => statusListeners.delete(onChange); },
      () => status,
    );
  }

  return {
    getSocket, useSocket, useMatchSocket, useWatchMatchSocket, useTournamentSocket,
    useSocketEvents, useConnectionStatus, setAccessTokenGetter,
  };
}
